import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    LinearProgress,
    IconButton,
    Paper,
    Divider,
    CircularProgress,
    Button
} from "@mui/material";
import {
    Close as CloseIcon,
    AccountTree as GitBranchIcon,
    Description as FileTextIcon,
    Layers as LayersIcon,
    MenuBook as BookOpenIcon,
    AutorenewRounded as LoaderIcon
} from "@mui/icons-material";
import fetchWithAuth from "@/api/secureFetch";
import { useAppSelector } from "@/state/hooks";
import { addUserAnswersApi } from "@/api/api";
import { submitJobToProcessor, getWorkProcessorUrl, streamJobUpdates, JobStreamUpdate } from "@/api/workProcessor";

// Backend URL from environment
const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

enum FeatureGenerationType {
    NewProduct = "NewProduct",
    NewFeature = "NewFeature",
    Existing = "Existing"
}

interface ProjectInfo {
    project_type: string;
    project_name: string;
    target_users: string;
    user_frustrations: string;
    solution_description: string;
    features: {
        description: string;
    } | null;
    use_jira_integration: boolean;
    selected_jira_project: string | null;
    selected_jira_project_id: string | null;
    jira_process_job_id?: string | null;
    selected_jira_tickets?: Array<{
        id: string;
        story_number: string;
        external_id: number;
        title: string;
    }>;
    selected_files?: Array<{
        fileid: number;
        filename: string;
    }>;
    team_composition: string;
    use_git_integration?: boolean;
    git_provider?: string;
    selected_git_repos?: Array<{
        clone_url: string;
        default_branch: string;
        description: string;
        forks_count: number;
        full_name: string;
        html_url: string;
        id: string;
        is_private: boolean;
        language: string | null;
        name: string;
        stargazers_count: number;
        url: string;
    }>;
    selected_git_branch?: string;
    git_repository_ids?: number[];
    use_azure_devops_integration?: boolean;
    selected_azure_devops_project?: string | null;
    selected_azure_devops_project_id?: string | null;
    azure_devops_process_job_id?: string | null;
}

interface UserProjectSetupProps {
    isOpen: boolean;
    onClose: () => void;
    projectInfo?: ProjectInfo;
}

// Helper function to determine generation type from project_type
const getGenerationType = (projectType: string): FeatureGenerationType => {
    console.log("[UserProjectSetup] Project Type:", projectType);

    if (projectType === "new_product") {
        return FeatureGenerationType.NewProduct;
    } else if (projectType === "new_feature") {
        return FeatureGenerationType.NewFeature;
    } else if (projectType === "existing_multiple" || projectType === "existing") {
        return FeatureGenerationType.Existing;
    } else {
        console.warn(`[UserProjectSetup] Unknown project_type: "${projectType}", defaulting to Existing`);
        return FeatureGenerationType.Existing; // Default fallback
    }
};

interface Step {
    title: string;
    icon: React.ReactNode;
    items: string[];
}

//const gitRepoFiles = [];

export const UserProjectSetup: React.FC<UserProjectSetupProps> = ({ isOpen, onClose, projectInfo }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [jiraTickets, setJiraTickets] = useState<string[]>([]);
    const [isLoadingTickets, setIsLoadingTickets] = useState(false);
    const [createdProject, setCreatedProject] = useState<any>(null);
    const [projectCreationStatus, setProjectCreationStatus] = useState<string[]>([]);
    const [hasCreatedProject, setHasCreatedProject] = useState(false);
    const [generatedFeatures, setGeneratedFeatures] = useState<string[]>([]);
    const [isGeneratingFeatures, setIsGeneratingFeatures] = useState(false);
    const [featureGenerationComplete, setFeatureGenerationComplete] = useState(false);
    const featureGenerationCompleteRef = useRef(false);
    const [featuresList, setFeaturesList] = useState<Array<{ feature_id: number; feature_name: string }>>([]);
    const featuresListRef = useRef<Array<{ feature_id: number; feature_name: string }>>([]);
    const [featureProgress, setFeatureProgress] = useState<{ current: number; total: number } | null>(null);
    const [workProductBuildStatus, setWorkProductBuildStatus] = useState<string[]>([]);
    const [isBuildingWorkProduct, setIsBuildingWorkProduct] = useState(false);
    const isBuildingWorkProductRef = useRef(false);
    const workProductBuildCompleteRef = useRef(false);
    const [workProductProgress, setWorkProductProgress] = useState<{ current: number; total: number } | null>(null);
    const [gitProcessingStatus, setGitProcessingStatus] = useState<string[]>([]);
    const [isProcessingGit, setIsProcessingGit] = useState(false);
    const isProcessingGitRef = useRef(false);
    const gitProcessingCompleteRef = useRef(false);
    const hasStartedGitProcessingRef = useRef(false);
    const [gitProcessingProgress, setGitProcessingProgress] = useState<{
        progress: number;
        files_processed: number;
        chunks_created: number;
        current_files: string[];
    } | null>(null);
    const hasStartedProcessRef = useRef(false);
    const hasStartedWorkProductBuildRef = useRef(false);
    const hasCreatedProjectRef = useRef(false);

    // Uploaded files processing state
    const [uploadedFilesStatus, setUploadedFilesStatus] = useState<string[]>([]);
    const [isProcessingUploadedFiles, setIsProcessingUploadedFiles] = useState(false);
    const isProcessingUploadedFilesRef = useRef(false);
    const uploadedFilesProcessingCompleteRef = useRef(false);
    const hasStartedUploadedFilesProcessingRef = useRef(false);

    const token = useAppSelector(state => state.clientToken.token);
    const userId = useAppSelector(state => state.userProfiles.userProfile?.userid);

    // Debug logging for incoming projectInfo
    useEffect(() => {
        if (isOpen && projectInfo) {
            console.log("[UserProjectSetup] Received projectInfo (FULL):", projectInfo);
            console.log("[UserProjectSetup] Received projectInfo (Selected Fields):", {
                project_type: projectInfo.project_type,
                project_name: projectInfo.project_name,
                features: projectInfo.features,
                target_users: projectInfo.target_users,
                user_frustrations: projectInfo.user_frustrations,
                solution_description: projectInfo.solution_description,
                team_composition: projectInfo.team_composition,
                use_git_integration: projectInfo.use_git_integration,
                git_repository_ids: projectInfo.git_repository_ids,
                use_jira_integration: projectInfo.use_jira_integration,
                selected_jira_project: projectInfo.selected_jira_project,
                selected_jira_project_id: projectInfo.selected_jira_project_id,
                jira_process_job_id: projectInfo.jira_process_job_id,
                // Azure DevOps fields
                use_azure_devops_integration: projectInfo.use_azure_devops_integration,
                selected_azure_devops_project: projectInfo.selected_azure_devops_project,
                selected_azure_devops_project_id: projectInfo.selected_azure_devops_project_id,
                azure_devops_process_job_id: projectInfo.azure_devops_process_job_id,
                selected_files: projectInfo.selected_files
            });
        }
    }, [isOpen, projectInfo]);

    // Conditionally build steps array based on integrations
    const shouldIncludeGitStep = projectInfo?.use_git_integration && projectInfo?.git_repository_ids && projectInfo.git_repository_ids.length > 0;
    const shouldIncludeJiraStep = projectInfo?.use_jira_integration === true;
    const shouldIncludeUploadedFilesStep = projectInfo?.selected_files && projectInfo.selected_files.length > 0;

    const steps: Step[] = [
        {
            title: "Creating Product Structure",
            icon: <FileTextIcon sx={{ fontSize: 28 }} />,
            items: projectCreationStatus
        },
        // Conditionally add Uploaded Files step
        ...(shouldIncludeUploadedFilesStep
            ? [
                  {
                      title: "Analyzing Uploaded Files",
                      icon: <FileTextIcon sx={{ fontSize: 28 }} />,
                      items: uploadedFilesStatus.length > 0 ? uploadedFilesStatus : ["Preparing to link uploaded files..."]
                  }
              ]
            : []),
        // Conditionally add Jira Tickets step (only when use_jira_integration is true)
        ...(shouldIncludeJiraStep
            ? [
                  {
                      title: "Analyzing Jira Tickets",
                      icon: <FileTextIcon sx={{ fontSize: 28 }} />,
                      items: jiraTickets
                  }
              ]
            : []),
        // Conditionally add Git Processing step
        ...(shouldIncludeGitStep
            ? [
                  {
                      title: "Processing Git Repository",
                      icon: <GitBranchIcon sx={{ fontSize: 28 }} />,
                      items: gitProcessingStatus.length > 0 ? gitProcessingStatus : ["Preparing to process git repository..."]
                  }
              ]
            : []),
        {
            title: "Parsing and contextualizing inputs",
            icon: <FileTextIcon sx={{ fontSize: 28 }} />,
            items: workProductBuildStatus.length > 0 ? workProductBuildStatus : ["Preparing to analyze work product..."]
        },
        {
            title: "Building Features and User Stories",
            icon: <LayersIcon sx={{ fontSize: 28 }} />,
            items: generatedFeatures.length > 0 ? generatedFeatures : ["Preparing to generate features..."]
        }
    ];

    // Create project when component opens
    useEffect(() => {
        const createProject = async () => {
            // Only create project once per modal open - use ref to prevent race conditions
            if (!isOpen || !projectInfo || hasCreatedProjectRef.current || !userId) {
                return;
            }

            // Set ref immediately to prevent duplicate executions
            hasCreatedProjectRef.current = true;

            // Use project_type directly (new format) or fall back to features.description (legacy)
            const projectTypeValue = projectInfo.project_type || projectInfo.features?.description || "existing";
            const generationType = getGenerationType(projectTypeValue);
            console.log("[UserProjectSetup] Determined generation type:", generationType, "from project_type:", projectTypeValue);

            setProjectCreationStatus(["Initializing product setup..."]);

            try {
                setProjectCreationStatus(prev => [...prev, "Creating product structure..."]);

                // Prepare project data with external link if Jira project is selected
                const projectData: any = {
                    userid: userId,
                    projectname: projectInfo.project_name || "New Project",
                    status: "Active",
                    projectdesc: projectInfo.project_name || "",
                    current: true
                };

                // Add external link fields if Jira project is selected
                if (projectInfo.selected_jira_project_id && !isNaN(Number(projectInfo.selected_jira_project_id))) {
                    projectData.external_link_type = "JIRA";
                    projectData.external_link_key = projectInfo.selected_jira_project_id;
                }
                // Add external link fields if Azure DevOps project is selected
                else if (projectInfo.use_azure_devops_integration && projectInfo.selected_azure_devops_project) {
                    projectData.external_link_type = "AZURE_DEVOPS";
                    projectData.external_link_key = projectInfo.selected_azure_devops_project;
                    // Store Azure DevOps-specific data in external_link
                    projectData.external_link = {
                        ...projectData.external_link,
                        organization: projectInfo.selected_azure_devops_project?.split('/')[0] || '',
                        projectId: projectInfo.selected_azure_devops_project_id || '',
                        projectName: projectInfo.selected_azure_devops_project || ''
                    };
                }

                // Build external_link object for git settings
                const externalLinkData: any = {};

                if (projectInfo.use_git_integration) {
                    externalLinkData.git_settings = {
                        use_git_integration: projectInfo.use_git_integration,
                        git_provider: projectInfo.git_provider,
                        selected_git_repos: projectInfo.selected_git_repos,
                        selected_git_branch: projectInfo.selected_git_branch,
                        git_repository_ids: projectInfo.git_repository_ids
                    };
                }

                // Add external_link to project data if we have git settings
                if (Object.keys(externalLinkData).length > 0) {
                    projectData.external_link = externalLinkData;
                }

                const response = await fetchWithAuth(`${BACKEND_URI}/api/project`, token || "", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(projectData)
                });

                if (response.ok) {
                    const data = await response.json();
                    setCreatedProject(data.project);
                    setProjectCreationStatus(prev => [...prev, `Project "${data.project.project_name}" created successfully`]);

                    // Save workproduct answers for the project
                    const projectId = data.project.project_id;

                    setProjectCreationStatus(prev => [...prev, "Saving product answers..."]);

                    console.log("STARTING TO SAVE ANSWERS", { projectInfo });

                    const answersToSave = [
                        { questionid: 20, sectionid: 1, answer_text: generationType, label: "project type" },
                        { questionid: 22, sectionid: 1, answer_text: projectInfo.target_users, label: "target users" },
                        { questionid: 23, sectionid: 1, answer_text: projectInfo.user_frustrations, label: "user frustrations" },
                        { questionid: 24, sectionid: 1, answer_text: projectInfo.solution_description, label: "solution description" },
                        { questionid: 25, sectionid: 1, answer_text: projectInfo.team_composition, label: "team composition" }
                    ];

                    for (const answer of answersToSave) {
                        try {
                            await addUserAnswersApi(
                                {
                                    projectid: projectId,
                                    questionid: answer.questionid,
                                    sectionid: answer.sectionid,
                                    answer_text: answer.answer_text
                                },
                                token || ""
                            );
                            setProjectCreationStatus(prev => [...prev, `Saved ${answer.label}`]);
                        } catch (error) {
                            console.error(`Error saving ${answer.label}:`, error);
                        }
                    }

                    // Associate git repository with project if git integration is enabled
                    if (projectInfo.use_git_integration && projectInfo.git_repository_ids && projectInfo.git_repository_ids.length > 0) {
                        try {
                            const gitRepoId = projectInfo.git_repository_ids[0];
                            setProjectCreationStatus(prev => [...prev, `Associating git repository ${gitRepoId} with project...`]);

                            const updateResponse = await fetchWithAuth(`${BACKEND_URI}/api/git/repositories/${gitRepoId}`, token || "", {
                                method: "PUT",
                                headers: {
                                    "Content-Type": "application/json"
                                },
                                body: JSON.stringify({
                                    project_id: projectId
                                })
                            });

                            if (updateResponse.ok) {
                                setProjectCreationStatus(prev => [...prev, `Git repository ${gitRepoId} linked to project`]);
                                console.log(`✅ Successfully linked git repository ${gitRepoId} to project ${projectId}`);
                            } else {
                                const errorText = await updateResponse.text();
                                console.error(`Failed to link git repository: ${errorText}`);
                                setProjectCreationStatus(prev => [...prev, `⚠️ Warning: Could not link git repository`]);
                            }
                        } catch (error) {
                            console.error("Error linking git repository to project:", error);
                            setProjectCreationStatus(prev => [...prev, `⚠️ Warning: Error linking git repository`]);
                        }
                    }

                    // Link Jira imported user stories to the project if jira_process_job_id is provided
                    if (projectInfo.use_jira_integration && projectInfo.jira_process_job_id) {
                        try {
                            setProjectCreationStatus(prev => [...prev, "Linking Jira tickets to project..."]);

                            const linkResponse = await fetchWithAuth(`${BACKEND_URI}/api/user-stories-jira-import/link-to-project`, token || "", {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json"
                                },
                                body: JSON.stringify({
                                    process_job_id: projectInfo.jira_process_job_id,
                                    project_id: projectId
                                })
                            });

                            if (linkResponse.ok) {
                                const linkResult = await linkResponse.json();
                                setProjectCreationStatus(prev => [...prev, `✅ Linked ${linkResult.updated_count} Jira ticket(s) to project`]);
                                console.log(`✅ Successfully linked ${linkResult.updated_count} Jira tickets to project ${projectId}`);
                            } else {
                                const errorText = await linkResponse.text();
                                console.error(`Failed to link Jira tickets: ${errorText}`);
                                setProjectCreationStatus(prev => [...prev, `⚠️ Warning: Could not link Jira tickets`]);
                            }
                        } catch (error) {
                            console.error("Error linking Jira tickets to project:", error);
                            setProjectCreationStatus(prev => [...prev, `⚠️ Warning: Error linking Jira tickets`]);
                        }
                    }

                    setProjectCreationStatus(prev => [...prev, "Product setup complete"]);
                    setHasCreatedProject(true);
                } else {
                    console.error("Failed to create project");
                    setProjectCreationStatus(prev => [...prev, "Error creating project"]);
                    setHasCreatedProject(true); // Mark as attempted even on error
                }
            } catch (error) {
                console.error("Error creating project:", error);
                setProjectCreationStatus(prev => [...prev, "Error creating project"]);
                setHasCreatedProject(true); // Mark as attempted even on error
            }
        };

        createProject();
    }, [isOpen, projectInfo, userId, token]);

    // Fetch Jira tickets when component opens
    useEffect(() => {
        const fetchJiraTickets = async () => {
            if (!isOpen) {
                return;
            }

            // If no Jira project ID is provided, use fallback placeholder tickets
            const jiraProjectId = projectInfo?.selected_jira_project;

            if (!jiraProjectId) {
                setJiraTickets(["No Jira project selected"]);
                setIsLoadingTickets(false);
                return;
            }

            setIsLoadingTickets(true);
            try {
                const response = await fetchWithAuth(`${BACKEND_URI}/api/jira-projects/${jiraProjectId}/user-stories-jira-import?summary=true`, token || "");

                if (response.ok) {
                    const data = await response.json();
                    const tickets = data.user_stories.map((story: any) => `${story.story_number}: ${story.title}`);
                    setJiraTickets(tickets.length > 0 ? tickets : ["No Jira tickets found for this project"]);
                } else {
                    console.error("Failed to fetch Jira tickets");
                    // Fallback to placeholder message
                    setJiraTickets(["Unable to load Jira tickets"]);
                }
            } catch (error) {
                console.error("Error fetching Jira tickets:", error);
                setJiraTickets(["Error loading Jira tickets"]);
            } finally {
                setIsLoadingTickets(false);
            }
        };

        fetchJiraTickets();
    }, [isOpen, projectInfo?.selected_jira_project]);

    // Process uploaded files - link them to the project when we reach that step
    useEffect(() => {
        const processUploadedFiles = async () => {
            // Calculate the uploaded files step index dynamically (it's step 1 if present)
            const uploadedFilesStepIndex = shouldIncludeUploadedFilesStep ? 1 : -1;

            // Only trigger when we reach the uploaded files step
            if (!isOpen || !createdProject || !projectInfo || currentStep !== uploadedFilesStepIndex || uploadedFilesStepIndex === -1) {
                return;
            }

            // Prevent multiple executions
            if (hasStartedUploadedFilesProcessingRef.current) {
                return;
            }

            hasStartedUploadedFilesProcessingRef.current = true;

            console.log("Starting uploaded files processing");

            setIsProcessingUploadedFiles(true);
            isProcessingUploadedFilesRef.current = true;
            setUploadedFilesStatus(["Linking uploaded files to product..."]);

            try {
                const projectId = createdProject.project_id;
                const selectedFiles = projectInfo.selected_files || [];

                if (selectedFiles.length === 0) {
                    setUploadedFilesStatus(prev => [...prev, "No files to link"]);
                    setIsProcessingUploadedFiles(false);
                    isProcessingUploadedFilesRef.current = false;
                    uploadedFilesProcessingCompleteRef.current = true;
                    return;
                }

                // Link each file to the project using update_file_scopes API
                // First fetch existing scopes to preserve them when adding new project association
                for (const file of selectedFiles) {
                    setUploadedFilesStatus(prev => [...prev, `Linking file: ${file.filename}...`]);

                    try {
                        // Fetch existing file scopes to preserve them
                        let existingProjectIds: number[] = [];
                        let existingFeatureIds: number[] = [];
                        let isCurrentlyGlobal = false;

                        try {
                            const scopeResponse = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/get_file_scopes`, token || "", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ file_ids: [file.fileid] })
                            });

                            if (scopeResponse.ok) {
                                const scopeData = await scopeResponse.json();
                                const existingScope = scopeData.scopes?.[file.fileid];
                                if (existingScope) {
                                    isCurrentlyGlobal = existingScope.global !== null;
                                    existingProjectIds = (existingScope.projects || []).map((p: { project_id: number }) => p.project_id);
                                    existingFeatureIds = (existingScope.features || []).map((f: { feature_id: number }) => f.feature_id);
                                    console.log(`Existing scope for file ${file.fileid}:`, { isCurrentlyGlobal, existingProjectIds, existingFeatureIds });
                                }
                            }
                        } catch (scopeError) {
                            console.warn(`Could not fetch existing scope for file ${file.fileid}, will create new association:`, scopeError);
                        }

                        // Merge new project ID with existing ones (avoid duplicates)
                        const mergedProjectIds = [...new Set([...existingProjectIds, projectId])];

                        const response = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/update_file_scopes`, token || "", {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json"
                            },
                            body: JSON.stringify({
                                fileid: file.fileid,
                                is_global: false, // Adding to a project removes global status
                                project_ids: mergedProjectIds,
                                feature_ids: existingFeatureIds
                            })
                        });

                        if (response.ok) {
                            setUploadedFilesStatus(prev => [...prev, `✅ Linked: ${file.filename}`]);
                            console.log(`Successfully linked file ${file.fileid} (${file.filename}) to project ${projectId}. Total projects: ${mergedProjectIds.length}`);
                        } else {
                            const errorText = await response.text();
                            console.error(`Failed to link file ${file.filename}: ${errorText}`);
                            setUploadedFilesStatus(prev => [...prev, `⚠️ Warning: Could not link ${file.filename}`]);
                        }
                    } catch (error) {
                        console.error(`Error linking file ${file.filename}:`, error);
                        setUploadedFilesStatus(prev => [...prev, `⚠️ Error linking ${file.filename}`]);
                    }
                }

                setUploadedFilesStatus(prev => [...prev, `✅ File linking complete! ${selectedFiles.length} file(s) processed`]);
                setIsProcessingUploadedFiles(false);
                isProcessingUploadedFilesRef.current = false;
                uploadedFilesProcessingCompleteRef.current = true;
                console.log("✅ Uploaded files processing completed successfully.");
            } catch (error) {
                console.error("Error processing uploaded files:", error);
                setUploadedFilesStatus(prev => [...prev, `❌ Error: ${error instanceof Error ? error.message : "Unknown error"}`]);
                setIsProcessingUploadedFiles(false);
                isProcessingUploadedFilesRef.current = false;
                uploadedFilesProcessingCompleteRef.current = true; // Mark complete even on error to not block
            }
        };

        processUploadedFiles();
    }, [isOpen, currentStep, createdProject, projectInfo, token, shouldIncludeUploadedFilesStep]);

    // Import features from pre-processing when we reach the feature generation step (dynamic index)
    // Section ID 232 contains the pre-processed features JSON from workproduct 185
    const FEATURES_SECTION_ID = [232, 238, 237];

    useEffect(() => {
        const importFeaturesFromPreprocessing = async () => {
            // Calculate the feature generation step index dynamically
            // Steps: Project(0) -> UploadedFiles?(1) -> Jira?(2) -> Git?(3) -> WorkProduct -> Features
            const baseIndex = 1; // After project creation
            const uploadedFilesOffset = shouldIncludeUploadedFilesStep ? 1 : 0;
            const jiraOffset = shouldIncludeJiraStep ? 1 : 0;
            const gitOffset = shouldIncludeGitStep ? 1 : 0;
            const workProductStepIndex = baseIndex + uploadedFilesOffset + jiraOffset + gitOffset;
            const featureStepIndex = workProductStepIndex + 1;

            // Only trigger when we reach the feature generation step
            if (!isOpen || !createdProject || !projectInfo || currentStep !== featureStepIndex) {
                // Silently skip - this is normal when component isn't at the right step
                return;
            }

            // Prevent multiple executions
            if (isGeneratingFeatures || generatedFeatures.length > 1) {
                console.log("Feature import already in progress or completed");
                return;
            }

            console.log("Starting feature import from pre-processing for project:", createdProject);

            // Reset refs for new generation
            featuresListRef.current = [];

            setIsGeneratingFeatures(true);
            setGeneratedFeatures(["Importing features from pre-processed data..."]);
            setFeatureProgress(null);

            try {
                const requestBody = {
                    projectid: createdProject.project_id,
                    sectionid: FEATURES_SECTION_ID
                };

                console.log("Feature import request body:", requestBody);

                const response = await fetchWithAuth(`${BACKEND_URI}/api/features/from-pre-processing`, token || "", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(requestBody)
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("Failed to import features:", response.status, errorText);
                    setGeneratedFeatures([`Error: Failed to import features - ${errorText}`]);
                    setIsGeneratingFeatures(false);
                    return;
                }

                const result = await response.json();
                console.log("Feature import result:", result);

                if (result.success) {
                    // Update UI with imported features
                    const importedFeatures = result.features || [];

                    setGeneratedFeatures(prev => [...prev, `Found ${importedFeatures.length} features to import...`]);

                    // Process each feature for display
                    for (let i = 0; i < importedFeatures.length; i++) {
                        const feature = importedFeatures[i];
                        setGeneratedFeatures(prev => [...prev, `Feature ${i + 1}: ${feature.feature_name}`]);

                        // Store feature info
                        const newFeature = {
                            feature_id: feature.feature_id,
                            feature_name: feature.feature_name
                        };
                        featuresListRef.current = [...featuresListRef.current, newFeature];
                        setFeaturesList(prev => [...prev, newFeature]);

                        // Update progress
                        setFeatureProgress({ current: i + 1, total: importedFeatures.length });
                    }

                    setGeneratedFeatures(prev => [...prev, `✅ Successfully imported ${importedFeatures.length} features with user stories!`]);
                    setFeatureGenerationComplete(true);
                    featureGenerationCompleteRef.current = true;

                    console.log(`✅ Feature import COMPLETE! Total features: ${featuresListRef.current.length}`, featuresListRef.current);
                } else {
                    setGeneratedFeatures(prev => [...prev, `Error: ${result.error || "Unknown error during import"}`]);
                }

                setIsGeneratingFeatures(false);
            } catch (error) {
                console.error("Error importing features:", error);
                setGeneratedFeatures(prev => [...prev, `Error importing features: ${error instanceof Error ? error.message : "Unknown error"}`]);
                setIsGeneratingFeatures(false);
            }
        };

        importFeaturesFromPreprocessing();
    }, [isOpen, currentStep, createdProject, projectInfo, token, shouldIncludeGitStep, shouldIncludeJiraStep, shouldIncludeUploadedFilesStep]);

    // Process Git Repository when we reach the git processing step (step index depends on whether git is enabled)
    useEffect(() => {
        const processGitRepository = async () => {
            // Determine the git step index dynamically
            // Steps: Project(0) -> UploadedFiles?(1) -> Jira?(2) -> Git?(3)
            const baseIndex = 1;
            const uploadedFilesOffset = shouldIncludeUploadedFilesStep ? 1 : 0;
            const jiraOffset = shouldIncludeJiraStep ? 1 : 0;
            const gitStepIndex = shouldIncludeGitStep ? baseIndex + uploadedFilesOffset + jiraOffset : -1;

            // Only trigger when we reach the git processing step
            if (!isOpen || !createdProject || !projectInfo || currentStep !== gitStepIndex || gitStepIndex === -1) {
                return;
            }

            // Prevent multiple executions
            if (hasStartedGitProcessingRef.current) {
                return;
            }

            hasStartedGitProcessingRef.current = true;

            console.log("Starting git repository processing");

            setIsProcessingGit(true);
            isProcessingGitRef.current = true;
            setGitProcessingStatus(["Starting git repository chunking process..."]);

            try {
                const projectId = createdProject.project_id;

                // Start the git chunking job
                setGitProcessingStatus(prev => [...prev, "Submitting chunking job..."]);
                const startUrl = getWorkProcessorUrl("/api/git/process-chunks");
                console.log("🔍 Git Processing - Constructed URL:", startUrl);
                console.log("🔍 Git Processing - Project ID:", projectId);
                console.log("🔍 Git Processing - DEV mode:", import.meta.env.DEV);
                console.log("🔍 Git Processing - WORK_PROCESSOR_API_URL:", import.meta.env.VITE_WORK_PROCESSOR_API_URL);

                const startResponse = await fetchWithAuth(startUrl, token || "", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ project_id: projectId })
                });

                if (!startResponse.ok) {
                    const errorText = await startResponse.text();
                    console.error("❌ Git chunking job failed:", startResponse.status, startResponse.statusText);
                    console.error("❌ Error response body:", errorText);
                    throw new Error(`Failed to start git chunking (${startResponse.status}): ${errorText}`);
                }

                const { job_id } = await startResponse.json();
                console.log("Git chunking job started with ID:", job_id);
                setGitProcessingStatus(prev => [...prev, `Job created: ${job_id}`]);

                // Start polling for job status every 2 seconds
                const pollInterval = setInterval(async () => {
                    try {
                        const statusUrl = getWorkProcessorUrl(`/api/git/chunks-job/${job_id}/status`);
                        const statusResponse = await fetchWithAuth(statusUrl, token || "");

                        if (!statusResponse.ok) {
                            console.error("Failed to get git job status:", statusResponse.status);
                            return;
                        }

                        const jobStatus = await statusResponse.json();
                        console.log("Git job status:", jobStatus);

                        // Check for completion
                        if (jobStatus.status === "COMPLETED") {
                            clearInterval(pollInterval);
                            setGitProcessingStatus(prev => [
                                ...prev,
                                `✅ Processing complete! ${jobStatus.files_processed || 0} files processed, ${jobStatus.chunks_created || 0} chunks created`
                            ]);
                            setIsProcessingGit(false);
                            isProcessingGitRef.current = false;
                            gitProcessingCompleteRef.current = true;
                            console.log("✅ Git repository processing completed successfully.");
                        } else if (jobStatus.status === "FAILED") {
                            clearInterval(pollInterval);
                            setGitProcessingStatus(prev => [...prev, `❌ Job failed: ${jobStatus.message || "Unknown error"}`]);
                            setIsProcessingGit(false);
                            isProcessingGitRef.current = false;
                        } else if (jobStatus.status === "PROCESSING") {
                            // Update progress information
                            if (jobStatus.progress !== undefined) {
                                setGitProcessingProgress({
                                    progress: jobStatus.progress,
                                    files_processed: jobStatus.files_processed || 0,
                                    chunks_created: jobStatus.chunks_created || 0,
                                    current_files: jobStatus.current_files || []
                                });
                            }

                            // Build status message
                            let statusMessage = jobStatus.message || "Processing...";

                            // Add current files being processed if available
                            if (jobStatus.current_files && jobStatus.current_files.length > 0) {
                                const filesList = jobStatus.current_files.slice(0, 3).join(", ");
                                const moreCount = jobStatus.current_files.length - 3;
                                statusMessage += ` - Currently processing: ${filesList}${moreCount > 0 ? ` and ${moreCount} more...` : ""}`;
                            }

                            setGitProcessingStatus(prev => {
                                // Only add if different from last status message
                                const lastMsg = prev[prev.length - 1];
                                if (lastMsg && lastMsg.startsWith(jobStatus.message)) {
                                    return prev; // Don't add duplicate progress messages
                                }
                                return [...prev, statusMessage];
                            });
                        }
                    } catch (error) {
                        console.error("Error polling git job status:", error);
                    }
                }, 2000); // Poll every 2 seconds

                // Cleanup on unmount
                return () => clearInterval(pollInterval);
            } catch (error) {
                console.error("Error processing git repository:", error);
                setGitProcessingStatus(prev => [...prev, `❌ Error: ${error instanceof Error ? error.message : "Unknown error"}`]);
                setIsProcessingGit(false);
                isProcessingGitRef.current = false;
            }
        };

        processGitRepository();
    }, [isOpen, currentStep, createdProject, projectInfo, token, shouldIncludeGitStep, shouldIncludeJiraStep, shouldIncludeUploadedFilesStep]);

    // Build work product 185 when we reach the work product step (dynamic index)
    useEffect(() => {
        const buildWorkProduct = async () => {
            // Calculate the work product step index dynamically based on all conditional steps
            // Steps: Project(0) -> UploadedFiles?(1) -> Jira?(2) -> Git?(3) -> WorkProduct
            const baseIndex = 1;
            const uploadedFilesOffset = shouldIncludeUploadedFilesStep ? 1 : 0;
            const jiraOffset = shouldIncludeJiraStep ? 1 : 0;
            const gitOffset = shouldIncludeGitStep ? 1 : 0;
            const workProductStepIndex = baseIndex + uploadedFilesOffset + jiraOffset + gitOffset;

            // Only trigger when we reach the work product step
            if (!isOpen || !createdProject || !userId || currentStep !== workProductStepIndex) {
                return;
            }

            // Prevent multiple executions
            if (hasStartedWorkProductBuildRef.current) {
                return;
            }

            hasStartedWorkProductBuildRef.current = true;

            console.log("Starting work product build for WorkProduct ID 185");

            setIsBuildingWorkProduct(true);
            isBuildingWorkProductRef.current = true;
            setWorkProductBuildStatus([]);

            try {
                const jobRequest = {
                    projectid: createdProject.project_id,
                    userid: userId,
                    workproductid: 185
                };

                console.log("Triggering work product build with request:", jobRequest);

                // Trigger the work product build using the proper API function
                const result = await submitJobToProcessor(jobRequest, token || "");
                const jobId = result.job_id;
                console.log("Work product build job created:", result, "Job ID:", jobId);

                if (!jobId) {
                    setWorkProductBuildStatus(prev => [...prev, "❌ Error: No job ID returned"]);
                    setIsBuildingWorkProduct(false);
                    isBuildingWorkProductRef.current = false;
                    return;
                }

                // Use centralized job status manager for efficient batch polling
                const handleUpdate = (update: JobStreamUpdate) => {
                    console.log("Job status update:", update);

                    // Parse section progress from message - message contains correct "section X of Y" text
                    // Note: current_section is the section ID, not the sequential number
                    let statusMessage = update.message || "Processing...";
                    const sectionMatch = statusMessage.match(/section (\d+) of (\d+)/i);

                    if (sectionMatch) {
                        // Message has the correct sequential numbers (e.g., "Processing section 5 of 7")
                        const currentSection = parseInt(sectionMatch[1]);
                        const totalSections = parseInt(sectionMatch[2]);
                        statusMessage = `Building Section ${currentSection} of ${totalSections}`;
                        setWorkProductProgress({ current: currentSection, total: totalSections });
                    } else if (update.sections_completed !== undefined && update.total_sections) {
                        // Fallback: use sections_completed + 1 as current section
                        const currentSection = update.sections_completed + 1;
                        statusMessage = `Building Section ${currentSection} of ${update.total_sections}`;
                        setWorkProductProgress({ current: currentSection, total: update.total_sections });
                    } else if (update.status === "ACCEPTED" || update.status === "PENDING") {
                        statusMessage = "Job queued, waiting to start...";
                    } else {
                        statusMessage = "Processing...";
                    }

                    setWorkProductBuildStatus(prev => {
                        if (prev[prev.length - 1] !== statusMessage) {
                            return [...prev, statusMessage];
                        }
                        return prev;
                    });
                };

                const handleComplete = (result: JobStreamUpdate) => {
                    console.log("🎉 Job completed!", result);
                    setWorkProductBuildStatus(prev => [...prev, "✅ Work product build completed!"]);
                    setIsBuildingWorkProduct(false);
                    isBuildingWorkProductRef.current = false;
                    workProductBuildCompleteRef.current = true;
                    console.log("✅ Work product build completed successfully. Ref set to:", workProductBuildCompleteRef.current);
                };

                const handleError = (error: Error) => {
                    console.error("Job error:", error);
                    setWorkProductBuildStatus(prev => [...prev, `❌ Build failed: ${error.message}`]);
                    setIsBuildingWorkProduct(false);
                    isBuildingWorkProductRef.current = false;
                };

                // Register job with centralized status manager
                const cancelStream = await streamJobUpdates(jobId, token || "", handleUpdate, handleComplete, handleError);

                // Cleanup on unmount
                return () => cancelStream();
            } catch (error) {
                console.error("Error building work product:", error);
                setWorkProductBuildStatus(prev => [...prev, "❌ Error building work product"]);
                setIsBuildingWorkProduct(false);
                isBuildingWorkProductRef.current = false;
            }
        };

        buildWorkProduct();
    }, [isOpen, currentStep, createdProject, userId, token, shouldIncludeGitStep, shouldIncludeJiraStep, shouldIncludeUploadedFilesStep]);

    useEffect(() => {
        if (!isOpen) {
            // Reset state when modal closes
            setCurrentStep(0);
            setProjectCreationStatus([]);
            setCreatedProject(null);
            setHasCreatedProject(false);
            hasCreatedProjectRef.current = false;
            setGeneratedFeatures([]);
            setIsGeneratingFeatures(false);
            setFeatureGenerationComplete(false);
            featureGenerationCompleteRef.current = false;
            setFeaturesList([]);
            featuresListRef.current = [];
            setFeatureProgress(null);
            setWorkProductBuildStatus([]);
            setIsBuildingWorkProduct(false);
            isBuildingWorkProductRef.current = false;
            workProductBuildCompleteRef.current = false;
            hasStartedWorkProductBuildRef.current = false;
            setWorkProductProgress(null);
            setGitProcessingStatus([]);
            setIsProcessingGit(false);
            isProcessingGitRef.current = false;
            gitProcessingCompleteRef.current = false;
            hasStartedGitProcessingRef.current = false;
            setGitProcessingProgress(null);
            hasStartedProcessRef.current = false;
            // Reset uploaded files state
            setUploadedFilesStatus([]);
            setIsProcessingUploadedFiles(false);
            isProcessingUploadedFilesRef.current = false;
            uploadedFilesProcessingCompleteRef.current = false;
            hasStartedUploadedFilesProcessingRef.current = false;
            return;
        }

        // Wait for tickets to load before starting
        if (isLoadingTickets) {
            return;
        }

        // Wait for project creation to complete (check once)
        if (currentStep === 0 && projectCreationStatus.length === 0 && !hasCreatedProject) {
            return;
        }

        // Only start the process once (using ref to avoid re-render loop)
        if (!hasStartedProcessRef.current) {
            hasStartedProcessRef.current = true;
            processStep(0);
        }
    }, [isOpen, isLoadingTickets, hasCreatedProject]);

    const processStep = (stepIndex: number) => {
        if (stepIndex >= steps.length) {
            // All steps complete, close modal
            setTimeout(() => {
                onClose();
            }, 500);
            return;
        }

        setCurrentStep(stepIndex);

        // Wait for async operations to complete before moving to next step
        const checkCompletion = () => {
            // Calculate dynamic step indices based on all conditional steps
            // Steps: Project(0) -> UploadedFiles?(1) -> Jira?(2) -> Git?(3) -> WorkProduct -> Features
            const baseIndex = 1;
            const uploadedFilesOffset = shouldIncludeUploadedFilesStep ? 1 : 0;
            const jiraOffset = shouldIncludeJiraStep ? 1 : 0;
            const gitOffset = shouldIncludeGitStep ? 1 : 0;

            const uploadedFilesStepIndex = shouldIncludeUploadedFilesStep ? 1 : -1;
            const jiraStepIndex = shouldIncludeJiraStep ? baseIndex + uploadedFilesOffset : -1;
            const gitStepIndex = shouldIncludeGitStep ? baseIndex + uploadedFilesOffset + jiraOffset : -1;
            const workProductStepIndex = baseIndex + uploadedFilesOffset + jiraOffset + gitOffset;
            const featureStepIndex = workProductStepIndex + 1;

            // Uploaded Files step - wait for uploaded files processing to complete
            if (stepIndex === uploadedFilesStepIndex && !uploadedFilesProcessingCompleteRef.current) {
                console.log(`⏳ Step ${stepIndex}: Waiting for uploaded files processing. complete ref: ${uploadedFilesProcessingCompleteRef.current}`);
                setTimeout(checkCompletion, 1000);
                return;
            }

            // Jira step - no async processing, just display, so it completes immediately
            // (Jira tickets are fetched on component load, not on step transition)

            // Git Processing step - wait for git processing to complete
            if (stepIndex === gitStepIndex && !gitProcessingCompleteRef.current) {
                console.log(`⏳ Step ${stepIndex}: Waiting for git processing. complete ref: ${gitProcessingCompleteRef.current}`);
                setTimeout(checkCompletion, 1000);
                return;
            }

            // Work Product Build step - wait for work product build to complete
            if (stepIndex === workProductStepIndex && !workProductBuildCompleteRef.current) {
                console.log(`⏳ Step ${stepIndex}: Waiting for work product build. complete ref: ${workProductBuildCompleteRef.current}`);
                setTimeout(checkCompletion, 1000);
                return;
            }

            // Feature Generation step - wait for feature generation to complete
            if (stepIndex === featureStepIndex && !featureGenerationCompleteRef.current) {
                console.log(`⏳ Step ${stepIndex}: Waiting for feature generation. complete ref: ${featureGenerationCompleteRef.current}`);
                setTimeout(checkCompletion, 1000);
                return;
            }

            // Move to next step immediately
            console.log(`✅ Step ${stepIndex} complete, moving to step ${stepIndex + 1}`);
            processStep(stepIndex + 1);
        };

        checkCompletion();
    };

    if (!isOpen) return null;

    const currentStepData = steps[currentStep];
    const progress = ((currentStep + 1) / steps.length) * 100;

    // Calculate step indices for loading state
    // Steps: Project(0) -> UploadedFiles?(1) -> Jira?(2) -> Git?(3) -> WorkProduct -> Features
    const baseIndex = 1;
    const uploadedFilesOffset = shouldIncludeUploadedFilesStep ? 1 : 0;
    const jiraOffset = shouldIncludeJiraStep ? 1 : 0;
    const gitOffset = shouldIncludeGitStep ? 1 : 0;

    const uploadedFilesStepIndex = shouldIncludeUploadedFilesStep ? 1 : -1;
    const jiraStepIndex = shouldIncludeJiraStep ? baseIndex + uploadedFilesOffset : -1;
    const gitStepIndex = shouldIncludeGitStep ? baseIndex + uploadedFilesOffset + jiraOffset : -1;
    const workProductStepIndex = baseIndex + uploadedFilesOffset + jiraOffset + gitOffset;
    const featureStepIndex = workProductStepIndex + 1;

    // Show loading state (thinking spinner) while any async operation is in progress
    const showLoadingState =
        (currentStep === 0 && projectCreationStatus.length === 0) ||
        (isProcessingUploadedFiles && currentStep === uploadedFilesStepIndex) ||
        (isLoadingTickets && currentStep === jiraStepIndex) ||
        (isProcessingGit && currentStep === gitStepIndex) ||
        (isBuildingWorkProduct && currentStep === workProductStepIndex) ||
        (isGeneratingFeatures && currentStep === featureStepIndex);

    return (
        <Dialog
            open={isOpen}
            onClose={(event, reason) => {
                // Only allow closing via the X button (not backdrop click or escape key)
                if (reason === "backdropClick" || reason === "escapeKeyDown") {
                    return;
                }
                onClose();
            }}
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    borderRadius: "8px",
                    width: "700px",
                    maxWidth: "90vw",
                    boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.1)"
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "24px",
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "18px",
                    fontWeight: 600,
                    lineHeight: "24px",
                    color: "#1A1A1A",
                    paddingBottom: "16px"
                }}
            >
                Getting your Product ready – this may take a few minutes
                <IconButton
                    onClick={onClose}
                    disabled={currentStep < steps.length - 1}
                    sx={{
                        color: "#999999",
                        padding: "4px"
                    }}
                >
                    <CloseIcon sx={{ fontSize: "16px" }} />
                </IconButton>
            </DialogTitle>
            <Divider sx={{ borderColor: "#E5E5E5" }} />

            {/* Overall Progress Bar */}
            <Box sx={{ padding: "16px 24px 0 24px" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#666666"
                        }}
                    >
                        Overall Progress
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#7D5AFF"
                        }}
                    >
                        Step {currentStep + 1} of {steps.length}
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        height: "6px",
                        borderRadius: "3px",
                        backgroundColor: "#E5E5E5",
                        "& .MuiLinearProgress-bar": {
                            backgroundColor: "#7D5AFF",
                            borderRadius: "3px"
                        }
                    }}
                />
            </Box>

            {/* Content */}
            <DialogContent
                sx={{
                    padding: "24px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px"
                }}
            >
                {/* Current Step Header */}
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#666666",
                            marginBottom: "4px"
                        }}
                    >
                        Current Step:
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "16px",
                            fontWeight: 600,
                            color: "#1A1A1A"
                        }}
                    >
                        {currentStepData.title}
                    </Typography>
                </Box>

                {/* All Steps with Active Highlighting */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", maxHeight: "400px", overflowY: "auto" }}>
                    {steps.map((step, stepIndex) => {
                        const isActive = stepIndex === currentStep;
                        const isComplete = stepIndex < currentStep;
                        const isPending = stepIndex > currentStep;

                        return (
                            <Box
                                key={stepIndex}
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "12px",
                                    padding: "12px",
                                    borderRadius: "8px",
                                    backgroundColor: isActive ? "#F0F9FF" : "#F8F9FA",
                                    border: isActive ? "2px solid #7D5AFF" : "1px solid #E5E5E5",
                                    transition: "all 0.3s ease",
                                    opacity: isPending ? 0.6 : 1
                                }}
                            >
                                {/* Status Icon */}
                                <Box
                                    sx={{
                                        width: "24px",
                                        height: "24px",
                                        borderRadius: "50%",
                                        backgroundColor: isComplete ? "#22C55E" : isActive ? "#7D5AFF" : "#E5E5E5",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        transition: "all 0.3s ease"
                                    }}
                                >
                                    {isComplete && <Typography sx={{ color: "white", fontSize: "14px", fontWeight: "bold" }}>✓</Typography>}
                                    {isActive && showLoadingState && <CircularProgress size={14} sx={{ color: "white" }} />}
                                    {isActive && !showLoadingState && <CircularProgress size={14} sx={{ color: "white" }} />}
                                </Box>

                                {/* Step Content */}
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Poppins', sans-serif",
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: isPending ? "#999999" : "#1A1A1A",
                                            marginBottom: "4px"
                                        }}
                                    >
                                        {step.title}
                                    </Typography>

                                    {/* Show items only for active step */}
                                    {isActive && step.items.length > 0 && (
                                        <Box
                                            sx={{
                                                mt: 1,
                                                maxHeight: "150px",
                                                overflowY: "auto",
                                                "&::-webkit-scrollbar": { width: "4px" },
                                                "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" }
                                            }}
                                        >
                                            {step.items.slice(-5).map((item, idx) => (
                                                <Typography
                                                    key={idx}
                                                    sx={{
                                                        fontFamily: "'Poppins', sans-serif",
                                                        fontSize: "12px",
                                                        fontWeight: 400,
                                                        color: "#666666",
                                                        marginTop: idx > 0 ? "4px" : "0"
                                                    }}
                                                >
                                                    • {item}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}

                                    {/* Git processing progress */}
                                    {isActive && stepIndex === gitStepIndex && gitProcessingProgress && (
                                        <Box sx={{ width: "100%", mt: "8px" }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={gitProcessingProgress.progress}
                                                sx={{
                                                    height: "6px",
                                                    borderRadius: "3px",
                                                    backgroundColor: "#E5E5E5",
                                                    "& .MuiLinearProgress-bar": {
                                                        backgroundColor: "#7D5AFF",
                                                        borderRadius: "3px"
                                                    }
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Poppins', sans-serif",
                                                    fontSize: "11px",
                                                    fontWeight: 400,
                                                    color: "#666666",
                                                    marginTop: "4px"
                                                }}
                                            >
                                                {gitProcessingProgress.progress}% • {gitProcessingProgress.files_processed} files •{" "}
                                                {gitProcessingProgress.chunks_created} chunks
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Work product progress */}
                                    {isActive && stepIndex === workProductStepIndex && workProductProgress && (
                                        <Box sx={{ width: "100%", mt: "8px" }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={(workProductProgress.current / workProductProgress.total) * 100}
                                                sx={{
                                                    height: "6px",
                                                    borderRadius: "3px",
                                                    backgroundColor: "#E5E5E5",
                                                    "& .MuiLinearProgress-bar": {
                                                        backgroundColor: "#7D5AFF",
                                                        borderRadius: "3px"
                                                    }
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Poppins', sans-serif",
                                                    fontSize: "11px",
                                                    fontWeight: 400,
                                                    color: "#666666",
                                                    marginTop: "4px"
                                                }}
                                            >
                                                Section {workProductProgress.current} of {workProductProgress.total}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Feature generation progress */}
                                    {isActive && stepIndex === featureStepIndex && featureProgress && (
                                        <Box sx={{ width: "100%", mt: "8px" }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={(featureProgress.current / featureProgress.total) * 100}
                                                sx={{
                                                    height: "6px",
                                                    borderRadius: "3px",
                                                    backgroundColor: "#E5E5E5",
                                                    "& .MuiLinearProgress-bar": {
                                                        backgroundColor: "#7D5AFF",
                                                        borderRadius: "3px"
                                                    }
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Poppins', sans-serif",
                                                    fontSize: "11px",
                                                    fontWeight: 400,
                                                    color: "#666666",
                                                    marginTop: "4px"
                                                }}
                                            >
                                                Feature {featureProgress.current} of {featureProgress.total}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* Success Message when all complete */}
                {currentStep === steps.length - 1 && !showLoadingState && (
                    <Box
                        sx={{
                            backgroundColor: "#D1FAE5",
                            border: "1px solid #6EE7B7",
                            borderRadius: "8px",
                            padding: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                        }}
                    >
                        <Typography sx={{ color: "#065F46", fontSize: "14px", fontWeight: 500 }}>✅ Product setup completed successfully!</Typography>
                    </Box>
                )}
            </DialogContent>

            <Divider sx={{ borderColor: "#E5E5E5" }} />

            {/* Footer */}
            <DialogActions
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: "16px 24px",
                    gap: "12px"
                }}
            >
                {currentStep === steps.length - 1 && !showLoadingState && (
                    <Button
                        onClick={onClose}
                        variant="contained"
                        sx={{
                            height: "36px",
                            padding: "0 16px",
                            borderRadius: "4px",
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: "14px",
                            fontWeight: 500,
                            backgroundColor: "#22C55E",
                            color: "white",
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: "#16A34A"
                            }
                        }}
                    >
                        Done
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
